import { SvgIconProps } from '@mui/material';
import { isAfter, isSameDay, isValid, isWithinInterval } from 'date-fns';
import format from 'date-fns/format';
import BannerCampaignIcon from 'modules/campaigns/components/common/icons/banner-campaign.icon';
import EmailCampaignIcon from 'modules/campaigns/components/common/icons/email-campaign.icon';
import PromotionCampaignIcon from 'modules/campaigns/components/common/icons/promotion-campaign.icon';
import SearchPositioningIcon from 'modules/campaigns/components/common/icons/search-positioning-campaign.icon';
import SponsorshipCampaignIcon from 'modules/campaigns/components/common/icons/sponsorship-campaign.icon';
import { CAMPAIGN_TYPE_ENUM } from 'modules/campaigns/constants';
import { CAMPAIGN_ITEM_STATUS_ENUM, CAMPAIGN_STATUS_ENUM } from 'modules/campaigns/constants/common/status.enum';
import { ICampaign } from 'modules/campaigns/interfaces';
import { IContact } from 'modules/contact/interfaces';

export const getCampaignTypeIcon = (type: CAMPAIGN_TYPE_ENUM, props?: SvgIconProps) => {
  const sx = {
    fill: '#B2833D',
  };

  switch (type.toUpperCase()) {
    case CAMPAIGN_TYPE_ENUM.banner:
      return <BannerCampaignIcon {...props} sx={{ ...sx, ...(props?.sx || {}) }} />;
    case CAMPAIGN_TYPE_ENUM.itemsPromotion:
      return <PromotionCampaignIcon {...props} sx={{ ...sx, ...(props?.sx || {}) }} />;
    case CAMPAIGN_TYPE_ENUM.sponsorship:
      return <SponsorshipCampaignIcon {...props} sx={{ ...sx, ...(props?.sx || {}) }} />;
    case CAMPAIGN_TYPE_ENUM.searchPositioning:
      return <SearchPositioningIcon {...props} sx={{ ...sx, ...(props?.sx || {}) }} />;
    case CAMPAIGN_TYPE_ENUM.email:
      return <EmailCampaignIcon {...props} sx={{ ...sx, ...(props?.sx || {}) }} />;
    default:
      return null;
  }
};

export const calculateContactGroupPrice = (contact: IContact, campaignPrice: number): number => {
  return (contact?.emailCount || 0) * campaignPrice;
};

export const formatDate = (date?: string | null, locale?: string) => {
  const dateFormat = locale?.startsWith('es') ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  if (date) {
    return format(new Date(date), dateFormat);
  }
  return '-';
};

export const isInExecution = (campaign?: ICampaign) => {
  let inRange = false;

  if (campaign?.startTime && campaign?.endTime) {
    const now = Date.now();
    const start = new Date(campaign?.startTime);
    const end = new Date(campaign?.endTime);
    inRange = isWithinInterval(now, { start, end }) && !isSameDay(now, end);
  }
  return campaign?.status === CAMPAIGN_STATUS_ENUM.PAID && inRange;
};

export const isExecuted = (campaign?: ICampaign) => {
  return campaign?.status === CAMPAIGN_STATUS_ENUM.COMPLETED;
};

export const campaignStatus = (campaign?: ICampaign): CAMPAIGN_STATUS_ENUM | undefined => {
  if (!campaign) return undefined;
  const { status } = campaign;

  if (status === CAMPAIGN_STATUS_ENUM.PAID) {
    if (isInExecution(campaign)) {
      return CAMPAIGN_STATUS_ENUM.IN_PROGRESS;
    }
    if (isExecuted(campaign)) {
      return CAMPAIGN_STATUS_ENUM.COMPLETED;
    }
  }

  return status;
};

export const campaignItemStatus = (
  start: string = '',
  end: string = '',
  campaignType: CAMPAIGN_TYPE_ENUM,
): CAMPAIGN_ITEM_STATUS_ENUM => {
  const now = Date.now();
  const startDate = new Date(start);
  const endDate = new Date(end);

  if (!isValid(startDate) || !isValid(endDate)) {
    return CAMPAIGN_ITEM_STATUS_ENUM.PENDING;
  }

  if (campaignType === CAMPAIGN_TYPE_ENUM.email) {
    if (isAfter(now, startDate) || isSameDay(now, startDate)) {
      return CAMPAIGN_ITEM_STATUS_ENUM.COMPLETED;
    }
    return CAMPAIGN_ITEM_STATUS_ENUM.PENDING;
  }

  const inRange = isWithinInterval(now, {
    start: startDate,
    end: endDate,
  });

  if (inRange && !isSameDay(now, endDate)) {
    return CAMPAIGN_ITEM_STATUS_ENUM.IN_PROGRESS;
  }

  if (isAfter(now, endDate) || isSameDay(now, endDate)) {
    return CAMPAIGN_ITEM_STATUS_ENUM.COMPLETED;
  }

  return CAMPAIGN_ITEM_STATUS_ENUM.PENDING;
};
